import { useEffect, useRef } from 'react'

import { useVirtualizer } from '@tanstack/react-virtual'

import { AssetListItem } from './AssetListItem'
import { AssetStateSummary } from './types'

interface AssetListSharedProps {
  assets: AssetStateSummary[]
  currentAssetId?: number
  onAssetClick: (asset: AssetStateSummary) => void
  onShowDetails?: () => void
  viewMode?: 'list' | 'details' // Optional for mobile
}

export const AssetListShared = ({
  assets,
  currentAssetId,
  onAssetClick,
  onShowDetails,
  viewMode = 'list',
}: AssetListSharedProps) => {
  const containerRef = useRef<HTMLDivElement>(null)

  const virtualizer = useVirtualizer({
    count: assets.length,
    getScrollElement: () => containerRef.current,
    estimateSize: () => (viewMode === 'list' ? 56 : 100),
    overscan: 5,
  })

  useEffect(() => {
    if (!currentAssetId) return

    const index = assets.findIndex((a) => a.id === currentAssetId)
    if (index === -1) return

    const visibleItems = virtualizer.getVirtualItems()
    const isVisible = visibleItems.some((item) => item.index === index)

    if (!isVisible) {
      virtualizer.scrollToIndex(index, { align: 'start', behavior: 'smooth' })
    }
  }, [currentAssetId, assets, virtualizer])

  return (
    <div className="flex-1 overflow-y-auto" ref={containerRef}>
      <div
        style={{
          height: `${virtualizer.getTotalSize()}px`,
          width: '100%',
          position: 'relative',
        }}
      >
        {virtualizer.getVirtualItems().map((virtualRow) => {
          const asset = assets[virtualRow.index]
          return (
            <AssetListItem
              key={asset.id}
              asset={asset}
              isSelected={asset.id === currentAssetId}
              onClick={() => onAssetClick(asset)}
              onShowDetails={onShowDetails}
              viewMode={viewMode}
              style={{
                position: 'absolute',
                top: 0,
                left: 0,
                width: '100%',
                transform: `translateY(${virtualRow.start}px)`,
              }}
            />
          )
        })}
      </div>
    </div>
  )
}
